import React, { useRef } from "react";
import ReactDOM from "react-dom";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { cryptiqueAnalyticsTheme as themeDefault } from "../theme";
import { formatNumber } from "../../../../shared/utils/numberFormatter";
import { makeFormatValue, getThresholdValues } from "../utils/axisFormat";

/**
 * Funnel line chart that mimics Mixpanel's funnel line visualization.
 * Shows conversion rate over time with tooltip.
 * Uses chart config (axis.leftY, colors) like InsightsChart when config is provided.
 * Shows conversion rate over time with tooltip:
 * - Title: Conversion Rate of X through Y – All Steps
 * - Date
 * - XX.XXXX% converted
 * - N users
 * - +/-Y.YY% from previous day
 *
 * Also supports breakdown mode with multiple segments:
 * - Pass breakdownSegments: [{ segment, points: [{ date, conversion, users, delta }] }]
 * - Renders one line per segment with legend + combined tooltip
 */
export default function FunnelLineChart({
  points = [],
  title,
  theme = themeDefault,
  height = 280,
  breakdownSegments = null,
  measuring = "conversion_rate",
  config,
}) {
  const containerRef = useRef(null);

  // Axis + formatting config (shared with other funnel components)
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const xAxis = config?.axis?.xAxis ?? {};
  const funnelAxis = config?.funnelAxis ?? "left";
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);
  const leftYVisible = yAxisConfig.visible !== false;
  const leftYLabel = yAxisConfig.label;
  const leftYScale =
    yAxisConfig && String(yAxisConfig.scale) === "log" ? "log" : "linear";
  const outOfBounds = yAxisConfig.outOfBounds ?? "hidden"; // "hidden" = cropped view to thresholds; "visible" = full chart
  const { min: minThreshold, max: maxThreshold } =
    getThresholdValues(yAxisConfig);
  const hasThresholds = minThreshold != null || maxThreshold != null;
  const xAxisVisible = xAxis.visible !== false;
  const xAxisLabel = xAxis.label || "";

  // Line color respects funnel config colors when provided
  const configColors =
    config && Array.isArray(config.colors) ? config.colors : [];
  const lineColor =
    configColors[0] || theme.accentChart || theme.primaryLight || "#1d0c46";

  const hasBreakdownSegments =
    Array.isArray(breakdownSegments) && breakdownSegments.length > 0;

  // When in breakdown mode, we expect data via breakdownSegments; in non-breakdown mode we use points
  if (
    (!Array.isArray(points) || points.length === 0) &&
    !hasBreakdownSegments
  ) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-sm" style={{ color: theme.textMuted }}>
          No data available
        </div>
      </div>
    );
  }

  // Color palette for breakdown segments
  const segmentColors = [
    "#7c3aed",
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#06b6d4",
    "#84cc16",
    "#f97316",
    "#ec4899",
    "#6366f1",
    "#14b8a6",
    "#a855f7",
    "#eab308",
    "#22c55e",
    "#06b6d4",
  ];

  // Build chart data + Y max depending on mode
  let chartData = points;
  let yMax = 100;

  if (hasBreakdownSegments) {
    // Collect all unique dates
    const dateSet = new Set();
    breakdownSegments.forEach((seg) => {
      (seg.points || []).forEach((p) => {
        if (p.date) dateSet.add(p.date);
      });
    });
    const allDates = Array.from(dateSet).sort((a, b) =>
      String(a).localeCompare(String(b))
    );

    const dataByDate = allDates.map((date) => {
      const row = { date };
      breakdownSegments.forEach((seg, idx) => {
        const match = (seg.points || []).find((p) => p.date === date);
        if (match) {
          // For conversion_rate, use conversion (percentage); for others, use value
          row[`seg_${idx}`] = measuring === 'conversion_rate' 
            ? (match.conversion ?? 0)
            : (match.value ?? match.conversion ?? 0);
          row[`seg_${idx}_users`] = match.users ?? 0;
          row[`seg_${idx}_value`] = match.value ?? match.conversion ?? 0;
          row[`seg_${idx}_delta`] = match.delta ?? null;
        } else {
          row[`seg_${idx}`] = null;
          row[`seg_${idx}_users`] = 0;
          row[`seg_${idx}_value`] = null;
          row[`seg_${idx}_delta`] = null;
        }
      });
      return row;
    });

    chartData = dataByDate;

    // Compute Y-axis max from all segment conversions
    let maxValue = 0;
    dataByDate.forEach((row) => {
      breakdownSegments.forEach((_, idx) => {
        const v = row[`seg_${idx}`];
        if (typeof v === "number" && v > maxValue) {
          maxValue = v;
        }
      });
    });
    yMax = maxValue > 0 ? maxValue * 1.2 : 100;
  } else {
    const maxValue = points.reduce(
      (max, p) =>
        p.conversion != null && p.conversion > max ? p.conversion : max,
      0
    );
    yMax = maxValue > 0 ? maxValue * 1.2 : 100;
    chartData = points;
  }

  // Y-axis domain: only axis bounds; do not modify data.
  // Default => full data range. When thresholds are defined and Out of Bounds = "hidden",
  // clip viewport to [Min, Max].
  let yDomain = [0, yMax];
  if (hasThresholds && outOfBounds === "hidden") {
    yDomain = [minThreshold ?? 0, maxThreshold ?? yMax];
  }
  if (leftYScale === "log" && (yDomain[0] == null || yDomain[0] <= 0)) {
    yDomain = [Math.max(0.01, yDomain[0] || 0.01), yDomain[1] ?? 100];
  }

  const yAxisLabelProps = leftYLabel
    ? {
        label: {
          value: leftYLabel,
          angle: -90,
          position: "insideLeft",
          style: { fontSize: 12, fill: "#6B7280" },
        },
      }
    : {};

  // Do not filter or modify the dataset; pass raw points so the line stays continuous.
  // Clipping: Recharts only enforces domain when allowDataOverflow=true (otherwise it auto-expands to fit data).
  // Hidden + thresholds => allowDataOverflow=true so [Min, Max] is enforced and viewport clips the line.
  const clipToThresholds = hasThresholds && outOfBounds === "hidden";

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      const month = d.toLocaleDateString("en-US", { month: "short" });
      const day = d.getDate();
      return `${month} ${day}`;
    } catch {
      return iso;
    }
  };

  const formatFullDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload || {};

    // Non-breakdown tooltip (single series)
    if (!hasBreakdownSegments) {
      const conv = Number(row.conversion || 0);
      const users = Number(row.users || 0);
      const delta =
        typeof row.delta === "number" && !Number.isNaN(row.delta)
          ? row.delta
          : null;

      const deltaAbs = delta != null ? Math.abs(delta) : 0;
      const deltaSign = delta != null && delta < 0 ? "-" : "+";
      const deltaColor =
        delta != null && delta < 0
          ? "#ef4444"
          : delta != null
          ? "#16a34a"
          : theme.textMuted;

      return (
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: `1px solid ${theme.border}`,
            padding: "12px 16px",
            minWidth: 240,
            fontFamily: "Raleway, sans-serif",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: theme.textPrimary,
              marginBottom: 4,
              lineHeight: 1.4,
            }}
          >
            {title || (measuring === 'conversion_rate' ? "Conversion Rate – All Steps" : "Funnel Value – All Steps")}
          </div>
          <div
            style={{
              fontSize: 12,
              color: theme.textMuted,
              marginBottom: 8,
            }}
          >
            {formatFullDate(row.date)}
          </div>
          <div
            style={{
              fontSize: 13,
              color: theme.textPrimary,
              marginBottom: 2,
            }}
          >
            {measuring === 'conversion_rate' 
              ? `${conv.toFixed(4)}% converted`
              : measuring === 'total_conversions'
              ? `${formatNumber(Number(conv || 0))} total conversions`
              : measuring === 'total_sessions'
              ? `${formatNumber(Number(conv || 0))} sessions`
              : measuring === 'unique_users'
              ? `${formatNumber(Number(conv || 0))} unique users`
              : `${formatNumber(Number(conv || 0))}`
            }
          </div>
          <div
            style={{
              fontSize: 13,
              color: theme.textPrimary,
              marginBottom: 4,
            }}
          >
            {formatNumber(users)} users
          </div>
          {delta != null && (
            <div
              style={{
                fontSize: 12,
                color: deltaColor,
              }}
            >
              {measuring === 'conversion_rate'
                ? `${deltaSign}${deltaAbs.toFixed(2)}% from previous day`
                : `${deltaSign}${formatNumber(deltaAbs)} from previous day`
              }
            </div>
          )}
        </div>
      );
    }

    // Breakdown tooltip: show header + one row per segment
    const segmentLines = (breakdownSegments || [])
      .map((seg, idx) => {
        const conv = row[`seg_${idx}`];
        if (conv == null) return null;
        const value = row[`seg_${idx}_value`] ?? conv;
        const users = Number(row[`seg_${idx}_users`] || 0);
        const deltaRaw = row[`seg_${idx}_delta`];
        const delta =
          typeof deltaRaw === "number" && !Number.isNaN(deltaRaw)
            ? deltaRaw
            : null;
        const deltaAbs = delta != null ? Math.abs(delta) : 0;
        const deltaSign = delta != null && delta < 0 ? "-" : "+";
        const deltaColor =
          delta != null && delta < 0
            ? "#ef4444"
            : delta != null
            ? "#16a34a"
            : theme.textMuted;

        // Determine display label and format based on measuring type
        let displayLabel, displayValue, deltaLabel;
        if (measuring === 'conversion_rate') {
          displayLabel = `${Number(conv || 0).toFixed(4)}% converted`;
          displayValue = Number(conv || 0);
          deltaLabel = delta != null ? `${deltaSign}${deltaAbs.toFixed(2)}% from previous day` : null;
        } else if (measuring === 'total_conversions') {
          displayLabel = `${formatNumber(Number(value || 0))} total conversions`;
          displayValue = Number(value || 0);
          deltaLabel = delta != null ? `${deltaSign}${formatNumber(deltaAbs)} from previous day` : null;
        } else if (measuring === 'total_sessions') {
          displayLabel = `${formatNumber(Number(value || 0))} sessions`;
          displayValue = Number(value || 0);
          deltaLabel = delta != null ? `${deltaSign}${formatNumber(deltaAbs)} from previous day` : null;
        } else if (measuring === 'unique_users') {
          displayLabel = `${formatNumber(Number(value || 0))} unique users`;
          displayValue = Number(value || 0);
          deltaLabel = delta != null ? `${deltaSign}${formatNumber(deltaAbs)} from previous day` : null;
        } else {
          displayLabel = `${formatNumber(Number(value || 0))}`;
          displayValue = Number(value || 0);
          deltaLabel = delta != null ? `${deltaSign}${formatNumber(deltaAbs)} from previous day` : null;
        }

        return {
          name: seg.segment || `Segment ${idx + 1}`,
          conv: Number(conv || 0),
          value: Number(value || 0),
          displayLabel,
          displayValue,
          users,
          delta,
          deltaAbs,
          deltaSign,
          deltaLabel,
          deltaColor,
          color: segmentColors[idx % segmentColors.length],
        };
      })
      .filter(Boolean);

    if (!segmentLines.length) return null;

    return (
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          border: `1px solid ${theme.border}`,
          padding: "12px 16px",
          minWidth: 260,
          fontFamily: "Raleway, sans-serif",
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: theme.textPrimary,
            marginBottom: 4,
            lineHeight: 1.4,
          }}
        >
          {title || "Conversion Rate – All Steps"}
        </div>
        <div
          style={{
            fontSize: 12,
            color: theme.textMuted,
            marginBottom: 8,
          }}
        >
          {formatFullDate(row.date)}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {segmentLines.map((seg, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: seg.color,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: theme.textPrimary,
                    maxWidth: 140,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={seg.name}
                >
                  {seg.name}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 2,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: theme.textPrimary,
                  }}
                >
                  {seg.displayLabel}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: theme.textPrimary,
                  }}
                >
                  {formatNumber(seg.users)} users
                </span>
                {seg.deltaLabel && (
                  <span
                    style={{
                      fontSize: 11,
                      color: seg.deltaColor,
                    }}
                  >
                    {seg.deltaLabel}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const PortalTooltip = (props) => {
    if (!props.active || !props.payload || !props.payload.length) return null;
    const rect = containerRef.current?.getBoundingClientRect();
    const coord = props.coordinate || {};
    const x = rect ? rect.left + (coord.x ?? 0) : coord.x ?? 0;
    const y = rect ? rect.top + (coord.y ?? 0) : coord.y ?? 0;
    const left = x + 12;
    const top = y - 8;

    return ReactDOM.createPortal(
      <div
        className="fixed pointer-events-none z-[9999]"
        style={{ left, top }}
      >
        <TooltipContent {...props} />
      </div>,
      document.body
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ height, margin: 0, padding: 0 }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart
          data={chartData}
          margin={{ top: 8, right: 24, left: 24, bottom: 24 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(0,0,0,0.08)"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            hide={!xAxisVisible}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
            axisLine={{ stroke: "rgba(0,0,0,0.15)" }}
            tickLine={false}
            tickFormatter={formatDate}
            {...(xAxisLabel ? {
              label: {
                value: xAxisLabel,
                position: "insideBottom",
                offset: -8,
                style: { fontSize: 12, fill: "#6B7280" }
              }
            } : {})}
          />
          <YAxis
            yAxisId="left"
            hide={!leftYVisible}
            width={leftYVisible ? 48 : 0}
            tick={{ fill: theme.textMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            domain={yDomain}
            scale={leftYScale}
            tickFormatter={formatYValue}
            allowDataOverflow={clipToThresholds}
            orientation={funnelAxis === "right" ? "right" : "left"}
            {...yAxisLabelProps}
          />
          <Tooltip content={<PortalTooltip />} />
          {hasBreakdownSegments ? (
            <>
              {breakdownSegments.map((seg, idx) => (
                <Line
                  yAxisId="left"
                  key={idx}
                  type="monotone"
                  dataKey={`seg_${idx}`}
                  stroke={segmentColors[idx % segmentColors.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 3.5,
                    fill: segmentColors[idx % segmentColors.length],
                  }}
                  name={seg.segment || `Segment ${idx + 1}`}
                  isAnimationActive={false}
                  connectNulls={true}
                />
              ))}
              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="circle"
                iconSize={8}
                wrapperStyle={{
                  paddingTop: 8,
                  fontSize: 11,
                  color: theme.textMuted,
                }}
              />
            </>
          ) : (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="conversion"
              stroke={lineColor}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: lineColor }}
              connectNulls
            />
          )}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}

